import React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import cx from "classnames";

import { LandingRiverFragment } from "types/api";
import Image from "components/Image";
import LandingMetadata from "./LandingMetadata";
import styles from "./LandingRiver.module.scss";
import gql from "graphql-tag";
import LandingRiverAd from "components/ads/LandingRiverInjector";

const LandingRiver = ({ edges, pageInfo }: LandingRiverFragment) => {
  const { pathname, asPath } = useRouter();
  const windowPathname = asPath.split("?")[0];
  return (
    <section>
      <ul className={styles.ul}>
        {edges.map((edge, i) => {
          return (
            edge?.node && (
              <React.Fragment key={edge.node.id}>
                {i > 0 && i % 5 === 0 && (
                  <li className={styles.liNativeAdWrapper}>
                    <LandingRiverAd
                      targetingPos={"native-river-" + i / 5}
                      className="s-native s-native--standard"
                    />
                  </li>
                )}
                <li className={styles.li}>
                  <article className={styles.promoItem}>
                    <a
                      href={edge.node.url}
                      className={styles.titleLink}
                      data-action={`click link - river - title ${i}`}
                    >
                      {edge.node.riverImage && (
                        <figure
                          className={cx(
                            styles.figure,
                            edge.node.__typename === "VideoArticle"
                              ? styles.video
                              : null
                          )}
                        >
                          <Image
                            src={edge.node.riverImage.url}
                            srcSet={edge.node.riverImage.srcSet || undefined}
                            width={edge.node.riverImage.width}
                            height={edge.node.riverImage.height}
                            alt={edge.node.riverImage.altText || ""}
                            lazy={true}
                            className={styles.image}
                          />
                        </figure>
                      )}
                      <h2 className={styles.title}>
                        <span
                          dangerouslySetInnerHTML={{
                            __html: edge.node.title,
                          }}
                        />
                      </h2>
                    </a>
                    {edge.node.dek && (
                      <p
                        className={styles.dek}
                        dangerouslySetInnerHTML={{ __html: edge.node.dek }}
                      />
                    )}
                    {edge.node.authors && edge.node.datePublished && (
                      <div className={styles.metadata}>
                        <LandingMetadata
                          {...edge.node}
                          analyticsSection="river"
                          riverIndex={i}
                        />
                      </div>
                    )}
                  </article>
                </li>
              </React.Fragment>
            )
          );
        })}
      </ul>
      <div className={styles.pagination}>
        {pageInfo.hasPreviousPage && (
          <Link
            href={{
              pathname,
              query: { before: pageInfo.startCursor },
            }}
            as={{
              pathname: windowPathname,
              query: { before: pageInfo.startCursor },
            }}
            shallow={true}
          >
            {/* eslint-disable-next-line */}
            <a className={styles.paginationLink}>Previous</a>
          </Link>
        )}
        {pageInfo.hasNextPage && (
          <Link
            href={{
              pathname,
              query: { after: pageInfo.endCursor },
            }}
            as={{
              pathname: windowPathname,
              query: { after: pageInfo.endCursor },
            }}
            shallow={true}
          >
            {/* eslint-disable-next-line */}
            <a className={styles.paginationLink}>More Stories</a>
          </Link>
        )}
      </div>
    </section>
  );
};

LandingRiver.fragment = gql`
  ${LandingMetadata.fragment}

  fragment landingPromoItem on PromoItem {
    id
    url
    title
    dek
    ...landingMetadata
    riverImage(width: 210, height: 130, sizes: ["1x", "2x"]) {
      id
      url
      srcSet
      width
      height
      altText
    }
  }

  fragment landingRiver on RiverConnection {
    pageInfo {
      hasNextPage
      hasPreviousPage
      startCursor
      endCursor
    }
    edges {
      cursor
      node {
        ...landingPromoItem
      }
    }
  }
`;

export default LandingRiver;
